import { getPlayerRoles } from "@/api/publicRequests/players/getPlayerRoles";
import { URLizeString } from "@/functions/capitalizeFirstLetters";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { APIStatus } from "../../api/APIStatus.type";
import * as apiFc24 from "../../api/apiFc24";
import { getPlayers } from "../../api/apiFc24";
import { getEvolvedPlayers } from "../../api/privateRequests/players/evolved-players/getEvolvedPlayers";
import { Status } from "../../api/privateRequests/players/getMyEvolutionPlayersStatus";
import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
  UpdateFilterType,
} from "../../components/organisms/Filters/PlayerFilters/PlayerFilters";
import { Caps } from "../../components/organisms/Players/MrDbListCardView/Sections/Attributes";
import { PlayersSortByType } from "../../components/organisms/PlayersToolbar/PlayersToolbar";
import { QueryParams } from "../../pages/Players/PlayersPage";
import { loadPlayerRoles } from "../squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { inGameAttributesDict } from "../squadBuilder/squadBuilderUtils";
import { IRootState } from "../store";

export const defaultPositions = [
  { category: "Attackers", label: "LW", id: 0 },
  { category: "Attackers", label: "ST", id: 1 },
  { category: "Attackers", label: "RW", id: 2 },
  { category: "Midfielders", label: "LM", id: 3 },
  { category: "Midfielders", label: "CAM", id: 4 },
  { category: "Midfielders", label: "RM", id: 5 },
  { category: "Midfielders", label: "CDM", id: 6 },
  { category: "Midfielders", label: "CM", id: 7 },
  { category: "Defenders", label: "LB", id: 8 },
  { category: "Defenders", label: "CB", id: 9 },
  { category: "Defenders", label: "RB", id: 10 },
  { category: "Defenders", label: "GK", id: 11 },
];

export type MetaRatingsPlayer = {
  resourceId: number;
  assetId: number;
  eaPlayerId: number | null;
  playerRoleId: number;
  name: string;
  rating: number;
  positions: string[];
  possiblePositions: string[];
  metaRating: number;
  metaRatingZeroChem: number;
  chemStyleId?: number;
  metaRatings?: {
    metaRating: number;
    playerRoleId: string;
    chemStyleId: number;
  }[];
  sbcStorage: boolean;
  newPlayer: boolean;
  owned: boolean;
  attributes: number[];
  countryId: number;
  leagueId: number;
  clubId: number;
  price: number;
  sbcPrice: number;
  skillMoves: number;
  weakFoot: number;
  untradeable: boolean;
  versionId: number;
  preferredPosition: string;
  metalId: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  playStylesPlus: number[];
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  cardName: string;
  rolesPlus: number[];
  evolutionPath?: number[];
  playStyles?: number[];
  canEvolve: boolean;
  availableEvos?: number;
  evoPathId?: number;
  status?: Status;
  caps?: Caps;
};

export type Focus = {
  name: string;
  description: string;
  advantages: string[];
  neutral: string[];
  disadvantages: string[];
  positions: string[];
};

export type PlayerRole = {
  id: number;
  name: string;
  eligiblePositions: string[];
  description: string;
  position: string;
  metaTiers: [
    {
      tierId: 0 | 1 | 2 | 3 | 4;
      minMetaRating: number;
    },
  ];
  index: number;
  focuses: Focus[];
  shortName?: string;
};

export type Tier = {
  tierName: "S" | "A+" | "A" | "B+" | "B";
  tierColor: string;
  tierTextColor: string;
  splitTierName?: string[];
};

export const tierIdToTier = (tierId: 0 | 1 | 2 | 3 | 4): Tier => {
  switch (tierId) {
    case 0:
      return {
        tierName: "S",
        tierColor: "bg-primary-light-blue",
        tierTextColor: "text-primary-light-blue",
        splitTierName: ["S"],
      };
    case 1:
      return {
        tierName: "A+",
        tierColor: "bg-ea-dark-green",
        tierTextColor: "text-ea-dark-green",
        splitTierName: ["A", "+"],
      };
    case 2:
      return {
        tierName: "A",
        tierColor: "bg-ea-light-green",
        tierTextColor: "text-ea-light-green",
        splitTierName: ["A"],
      };
    case 3:
      return {
        tierName: "B+",
        tierColor: "bg-tier-yellow",
        tierTextColor: "text-tier-yellow",
        splitTierName: ["B", "+"],
      };
    case 4:
      return {
        tierName: "B",
        tierColor: "bg-ea-orange",
        tierTextColor: "text-ea-orange",
        splitTierName: ["B"],
      };
  }
};

type PlayStyle = {
  name: string;
  category: string;
};

export type PlayersFrom = "market" | "both" | "club";

export type PlayStylesDict = {
  [id: number]: PlayStyle;
};

export type SelectedPlayStyle = {
  id: number;
  plus: boolean;
};

type PlayersState = {
  players: MetaRatingsPlayer[];
  myEvolvedPlayers: MetaRatingsPlayer[];
  totalPages: number;
  playerRoles: PlayerRole[];
  playStylesDict: PlayStylesDict;
  playersStatus: APIStatus;
  initialPlayerDataStatus: APIStatus;
  myEvolvedPlayersStatus: APIStatus;
  requestNo: number;
  playerFilters: (RangeFilterType | SelectorFilterType)[];
};

const initialState: PlayersState = {
  players: [],
  myEvolvedPlayers: [],
  totalPages: 1,
  playerRoles: [],
  playStylesDict: {},
  playersStatus: "idle",
  initialPlayerDataStatus: "idle",
  myEvolvedPlayersStatus: "idle",
  requestNo: 0,
  playerFilters: [
    {
      label: "Owned Players",
      key: "ownedOrEvolved",
      type: "SELECTOR_FILTER",
      options: [
        { id: 0, label: "Only Owned Players", category: "Owned" },
        { id: 1, label: "Only Evolved Players", category: "Evolved" },
      ],
      selectedOptions: [],
    },

    {
      label: "Price",
      key: "price",
      type: "RANGE_FILTER",
      range: [0, 9999999],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Meta Rating",
      key: "metaRating",
      type: "RANGE_FILTER",
      range: [0, 99],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Rating",
      key: "rating",
      type: "RANGE_FILTER",
      range: [0, 99],
      minValue: null,
      maxValue: null,
    },
    {
      label: "Past and Present Clubs",
      key: "pastAndPresentClubs",
      shortLabel: "Past and Present Clubs",
      type: "SELECTOR_FILTER",
      options: [
        {
          id: 1,
          label: "✔️",
          category: "Past and Present",
        },
      ],
      selectedOptions: [],
    },
    {
      label: "Country",
      key: "country",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Club",
      key: "club",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "League",
      key: "league",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Position",
      key: "position",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [...defaultPositions].map((position, i) => ({
        id: i,
        label: position.label,
        category: position.category,
      })),
      selectedOptions: [],
    },
    {
      label: "Rarity",
      key: "rarity",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "PlayStyles",
      key: "playStyles",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "PlayStyles+",
      key: "playStylesPlus",
      type: "SELECTOR_FILTER",
      options: [],
      selectedOptions: [],
    },
    {
      label: "Skill Moves",
      shortLabel: "SM",
      key: "skillMoves",
      type: "SELECTOR_FILTER",
      options: [
        { id: 1, label: "1", category: "Skill Moves" },
        { id: 2, label: "2", category: "Skill Moves" },
        { id: 3, label: "3", category: "Skill Moves" },
        { id: 4, label: "4", category: "Skill Moves" },
        { id: 5, label: "5", category: "Skill Moves" },
      ],
      selectedOptions: [],
    },
    {
      label: "Weak Foot",
      shortLabel: "WF",
      key: "weakFoot",
      type: "SELECTOR_FILTER",
      options: [
        { id: 1, label: "1", category: "Weak Foot" },
        { id: 2, label: "2", category: "Weak Foot" },
        { id: 3, label: "3", category: "Weak Foot" },
        { id: 4, label: "4", category: "Weak Foot" },
        { id: 5, label: "5", category: "Weak Foot" },
      ],
      selectedOptions: [],
    },
    {
      label: "Preferred Foot",
      shortLabel: "PF",
      key: "preferredFoot",
      type: "SELECTOR_FILTER",
      useLabelAsId: true,
      options: [
        { id: 0, label: "Left", category: "Preferred Foot" },
        { id: 1, label: "Right", category: "Preferred Foot" },
      ],
      selectedOptions: [],
    },
    ...Object.entries(inGameAttributesDict).map(
      ([key, attribute]) =>
        ({
          label: attribute.label,
          key,
          type: "RANGE_FILTER",
          range: [1, 99],
          minValue: null,
          maxValue: null,
        }) as RangeFilterType,
    ),
  ],
};

export const fetchPlayers = createAsyncThunk(
  "fetch-players",
  async (
    {
      page,
      sort,
      myClub,
      myEvolvedPlayers,
      chainedEvo,
      excludePaidEvo,
      excludeCompletedEvolutions,
      playerRoleId,
      queryParams,
      evolutions,
      evoIds,
      searchText,
    }: {
      page: number;
      sort: PlayersSortByType;
      myClub?: boolean;
      myEvolvedPlayers?: boolean;
      chainedEvo?: boolean;
      excludePaidEvo?: boolean;
      excludeCompletedEvolutions?: boolean;
      playerRoleId?: number;
      queryParams?: QueryParams[];
      evolutions?: boolean;
      evoIds?: number[];
      requestNo?: number;
      searchText?: string;
    },
    { dispatch, getState },
  ) => {
    const { playersReducer } = getState() as IRootState;
    let { requestNo } = playersReducer;
    requestNo += 1;
    dispatch(incrementRequestNo(requestNo));
    const res = await getPlayers({
      page,
      sort,
      myClub,
      myEvolvedPlayers,
      chainedEvo,
      excludePaidEvo,
      excludeCompletedEvolutions,
      playerRoleId,
      queryParams,
      evolutions,
      evoIds,
      searchText,
    });
    return {
      ...res,
      requestNo,
    };
  },
);

export const getMyEvolutionPlayers = createAsyncThunk(
  "get-evolution-players",
  async ({
    sort,
    playerRoleId,
  }: {
    sort: PlayersSortByType;
    playerRoleId: number;
  }) => {
    const res = await getEvolvedPlayers({
      sort,
      playerRoleId,
    });
    return res;
  },
);

type InitialPlayerData = {
  playerRoles: PlayerRole[];
  playStyles: PlayStylesDict;
};

export const bodyTypeIdToName = {
  1: ["Avg & Lean"],
  2: ["Avg & Normal"],
  3: ["Avg & Stocky"],
  4: ["Tall & Lean"],
  5: ["Tall & Normal"],
  6: ["Tall & Stocky"],
  7: ["Short & Lean"],
  8: ["Short & Normal"],
  9: ["Short & Stocky"],
  11: ["Very Tall & Lean"],
  12: ["Unique", "Lean"],
  13: ["Unique", "Normal"],
  14: ["Unique", "Stocky"],
  15: ["-", "-"],
};

export const getRolesPlusOrPlusPlus = ({
  playerRoleId,
  rolesPlus,
}: {
  playerRoleId: number;
  rolesPlus: number[];
}): "plus" | "plusPlus" | null => {
  if (rolesPlus?.includes(playerRoleId + 100)) return "plusPlus";
  if (rolesPlus?.includes(playerRoleId)) return "plus";
  return null;
};

export const matchRoleIdFromPlusPlus = ({
  playerRoleId,
  playerRoleIdFromPlusPlus,
}: {
  playerRoleId: number;
  playerRoleIdFromPlusPlus: number;
}) =>
  playerRoleId === playerRoleIdFromPlusPlus ||
  playerRoleId === playerRoleIdFromPlusPlus - 100;

export const getRoleFromPlusPlus = ({
  playerRoleIdFromPlusPlus,
  playerRoles,
}: {
  playerRoleIdFromPlusPlus: number;
  playerRoles: PlayerRole[];
}) => {
  return playerRoles.find((role) =>
    matchRoleIdFromPlusPlus({
      playerRoleId: role.id,
      playerRoleIdFromPlusPlus,
    }),
  );
};

export const generateNavigationUrl = ({
  playerRoleId,
  resourceId,
  name,
  versionName,
  evolutionPath,
  evoPathId,
  // canEvolve,
  myClub,
  chemistry, // TDOO: Add these for squad builder
  eaPlayerId, // TODO: Add these for squad builder
  chemstyleId, // TODO: Add these for squad builder
}: {
  playerRoleId: number;
  resourceId: number;
  name: string;
  versionName?: string;
  evolutionPath?: number[];
  evoPathId?: number;
  canEvolve?: boolean;
  myClub?: boolean;
  chemistry?: number;
  eaPlayerId?: number;
  chemstyleId?: number;
}) => {
  const basePath = myClub
    ? "/my-club"
    : evolutionPath
      ? "/evolutions"
      : "/players";
  const queryParams = new URLSearchParams();

  queryParams.set("player-role-id", playerRoleId?.toString());

  if (evolutionPath) {
    queryParams.set("evolution-path", evolutionPath.toString());
  }
  if (evoPathId) {
    queryParams.set("evo-path-id", evoPathId.toString());
  }

  return `${basePath}/${URLizeString(name)}${versionName ? `/${URLizeString(versionName)}` : ""}/${resourceId}?${queryParams.toString()}`;
};

export const getInitialPlayerData = createAsyncThunk<InitialPlayerData>(
  "get-initial-player-data",
  async (_, { dispatch }) => {
    const [playerRoles, playStyles] = await Promise.all([
      getPlayerRoles(),
      apiFc24.getPlayStyles(),
    ]);
    const sortedPlayerRoles = playerRoles?.sort((a, b) => b.id - a.id);
    dispatch(loadPlayerRoles(sortedPlayerRoles));

    return {
      playerRoles: sortedPlayerRoles,
      playStyles,
    };
  },
);

const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    incrementRequestNo: (state, action: PayloadAction<number>) => {
      state.requestNo = action.payload;
    },
    updateFilterOptions: (
      state,
      action: PayloadAction<{ key: string; options: SelectorOption[] }>,
    ) => {
      const filter = state.playerFilters.find(
        (f) => f.key === action.payload.key,
      );
      if (filter?.type === "SELECTOR_FILTER") {
        filter.options = action.payload.options;
      }
    },
    updateFilter: (state, action: PayloadAction<UpdateFilterType>) => {
      const { key } = action.payload;

      const filter = state.playerFilters.find((f) => f.key === key);

      if (filter?.type === "RANGE_FILTER") {
        const { min, max } = action.payload;
        if (min !== undefined) filter.minValue = min;
        if (max !== undefined) filter.maxValue = max;
      } else if (filter?.type === "SELECTOR_FILTER") {
        const { value } = action.payload;
        const isSelected = filter.selectedOptions.includes(value);

        if (filter.key === "ownedOrEvolved") {
          filter.selectedOptions = isSelected ? [] : [value];
        } else {
          if (isSelected)
            filter.selectedOptions = filter.selectedOptions.filter(
              (so) => so !== value,
            );
          else filter.selectedOptions.push(value);
        }
      }
    },
    clearAllFilters: (state) => {
      state.playerFilters.forEach((filter) => {
        if (filter.type === "RANGE_FILTER") {
          filter.minValue = null;
          filter.maxValue = null;
        } else if (filter.type === "SELECTOR_FILTER") {
          filter.selectedOptions = [];
        }
      });
    },
    clearFilter: (state, action: PayloadAction<string>) => {
      const filter = state.playerFilters.find((f) => f.key === action.payload);

      if (filter?.type === "RANGE_FILTER") {
        filter.minValue = null;
        filter.maxValue = null;
      } else if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = [];
      }
    },
    clearFilterOption: (
      state,
      action: PayloadAction<{ filterKey: string; option: number }>,
    ) => {
      const { filterKey, option } = action.payload;

      const filter = state.playerFilters.find((f) => f.key === filterKey);

      if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = filter.selectedOptions.filter(
          (so) => so !== option,
        );
      }
    },
    updateMyEvolvedPlayersStatus: (
      state,
      action: PayloadAction<
        {
          evoPathId: number;
          availableEvos: number;
          status: Status;
        }[]
      >,
    ) => {
      const updatedPlayers = state.myEvolvedPlayers.map((player) => {
        const updatedPlayer = action.payload.find(
          (p) => p.evoPathId === player.evoPathId,
        );
        if (updatedPlayer) {
          player.availableEvos = updatedPlayer.availableEvos;
          player.status = updatedPlayer.status;
        }
        return player;
      });
      state.myEvolvedPlayers = updatedPlayers;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayers.fulfilled, (state, action) => {
        if (action.payload.requestNo !== state.requestNo) {
          return;
        }
        state.players = action.payload.players;
        state.totalPages = action.payload.totalPages;
        state.playersStatus = "fulfilled";
      })
      .addCase(fetchPlayers.pending, (state) => {
        state.playersStatus = "pending";
      })
      .addCase(fetchPlayers.rejected, (state) => {
        state.playersStatus = "rejected";
      })
      .addCase(
        getInitialPlayerData.fulfilled,
        (state, action: PayloadAction<InitialPlayerData>) => {
          state.playStylesDict = action.payload.playStyles;
          state.playerRoles = action.payload.playerRoles;

          const playStyleFilter = state.playerFilters.find(
            (f) => f.key === "playStyles",
          ) as SelectorFilterType;
          const playStylePlusFilter = state.playerFilters.find(
            (f) => f.key === "playStylesPlus",
          ) as SelectorFilterType;

          const { playStyles } = action.payload;
          playStyleFilter.options = Object.entries(playStyles).map(
            ([id, playStyle]) => ({
              id: parseInt(id),
              label: playStyle.name,
              value: id,
              category: playStyle.category,
            }),
          );

          playStylePlusFilter.options = Object.entries(playStyles).map(
            ([id, playStyle]) => ({
              id: parseInt(id),
              label: playStyle.name,
              value: id,
              category: playStyle.category,
            }),
          );
          state.initialPlayerDataStatus = "fulfilled";
        },
      )
      .addCase(getInitialPlayerData.pending, (state) => {
        state.initialPlayerDataStatus = "pending";
      })
      .addCase(getInitialPlayerData.rejected, (state) => {
        state.initialPlayerDataStatus = "rejected";
      })
      .addCase(getMyEvolutionPlayers.pending, (state) => {
        state.myEvolvedPlayersStatus = "pending";
      })
      .addCase(getMyEvolutionPlayers.fulfilled, (state, action) => {
        state.myEvolvedPlayersStatus = "fulfilled";
        state.myEvolvedPlayers = action.payload;
      })
      .addCase(getMyEvolutionPlayers.rejected, (state) => {
        state.myEvolvedPlayersStatus = "rejected";
      });
  },
});

export const {
  incrementRequestNo,
  updateFilter,
  updateFilterOptions,
  clearAllFilters,
  clearFilter,
  clearFilterOption,
  updateMyEvolvedPlayersStatus,
} = playersSlice.actions;

export const playersSelector = (state: IRootState) => state.playersReducer;

export const activeFiltersSelector = (state: IRootState) => {
  const { playerFilters } = state.playersReducer;
  return playerFilters.filter((filter) => {
    if (filter.key === "ownedOrEvolved") return false;
    if (filter.type === "RANGE_FILTER")
      return filter.minValue !== null || filter.maxValue !== null;
    else return filter.selectedOptions.length > 0;
  });
};

export const ownedOrEvolvedSelector = (state: IRootState) => {
  const { playerFilters } = state.playersReducer;
  const filter = playerFilters.find(
    (f) => f.key === "ownedOrEvolved",
  ) as SelectorFilterType;
  const selectedOption = filter.options.find(
    (o) => o.id === filter?.selectedOptions[0],
  );
  return selectedOption;
};

export const playerFiltersAsQueryParamsSelector = (state: IRootState) => {
  let queryParams = [];
  const { playerFilters } = state.playersReducer;
  playerFilters.forEach((filter) => {
    if (filter.type === "RANGE_FILTER") {
      if (filter.minValue > 0)
        queryParams.push({
          [`${filter.key}Min`]: [filter.minValue],
        });

      if (filter.maxValue > 0)
        queryParams.push({
          [`${filter.key}Max`]: [filter.maxValue],
        });
    } else if (
      filter.type === "SELECTOR_FILTER" &&
      filter.selectedOptions.length > 0
    ) {
      if (filter.key === "ownedOrEvolved") return;

      queryParams.push({
        [filter.key]: filter.useLabelAsId
          ? filter.selectedOptions.map(
              (sp) => filter.options.find((p) => p.id === sp).label,
            )
          : filter.selectedOptions,
      });
    }
  });
  return queryParams;
};

export const tierSelector =
  (metaRating: number, playerRoleId: number) => (state: IRootState) => {
    const { playerRoles } = state.playersReducer;
    const playerRole = playerRoles.find((a) => a.id === playerRoleId);
    if (!playerRole) return null;

    const roundedMetaRating = Math.round(metaRating * 10) / 10;

    const tier = playerRole.metaTiers.find(
      (t) => roundedMetaRating >= t.minMetaRating,
    );

    if (!tier) return null;

    return tierIdToTier(tier.tierId);
  };

export const playerRoleLabelSelector =
  ({
    playerRoleId,
    appendPosition = true,
    shortName = false,
  }: {
    playerRoleId: number;
    appendPosition?: boolean;
    shortName?: boolean;
  }) =>
  ({ playersReducer }: IRootState) => {
    const playerRole = playersReducer.playerRoles.find(
      (pr) => pr.id === playerRoleId,
    );
    if (!playerRole) return "";
    if (!appendPosition) return playerRole.name;
    if (shortName) return playerRole.shortName;
    return playerRole?.name
      ? playerRole?.name + " " + playerRole?.position
      : "";
  };

export const getPlayerRoleByIdSelector =
  ({ playerRoleId }: { playerRoleId: number }) =>
  ({ playersReducer }: IRootState) => {
    return playersReducer.playerRoles.find((pr) => pr.id === playerRoleId);
  };

export const playerRoleByPositionSelector =
  ({ position }: { position: string }) =>
  ({ playersReducer }: IRootState) => {
    return playersReducer.playerRoles.filter(
      (playerRole) => playerRole.position === position,
    );
  };

export default playersSlice.reducer;
